function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import * as React from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'common/i18n/hooks';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { TooltipReact } from 'common/components/tooltip';
import { Placement } from 'common/components/tooltip/types';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { TrackingUsageContext } from 'common/report/tracking-setup';
import { DateRangeType } from 'common/types/reports/bookmark';
import { getCurrentMoment } from 'common/util/date';
import { DateRange, DateRangeAttributes } from 'common/widgets/date-range-picker/models/date-range';
import { PropertyFilterDateRange } from 'common/widgets/date-range-picker/models/property-filter-date-range';
import { DisplayContext } from 'common/widgets/date-range-picker/models/types';
import { DateRangePickerReact } from 'common/widgets/date-range-picker-new';
import { getDateRangeLabel } from 'common/widgets/date-range-picker-new/util';
import { getDateRangeTooltipRenderer } from 'common/widgets/date-range-preset-bar/date-range-tooltip';
import { FilterValueProps } from '../types';
export function FilterDateValue(props: FilterValueProps) {
  const currentProject = useCurrentProject().get().value;
  const {
    t,
    print
  } = useTranslation();
  /**
   * All possible date filters are expressed as a relative or absolute date selection
   * we express date filters in terms of these 2 primitives
   */
  const selectedDateAttrs = new DateRange(props.filter.filterValue as DateRangeAttributes).getAttributes();
  switch (selectedDateAttrs.type) {
    case DateRangeType.NotOn:
      selectedDateAttrs.type = DateRangeType.On;
      break;
    case DateRangeType.NotBetween:
      selectedDateAttrs.type = DateRangeType.Between;
      break;
    case DateRangeType.RelativeBefore:
    case DateRangeType.NotRelativeAfter:
      selectedDateAttrs.type = DateRangeType.RelativeAfter;
      break;
    case DateRangeType.Before:
      selectedDateAttrs.type = DateRangeType.On;
      selectedDateAttrs.from = selectedDateAttrs.to;
      break;
  }
  const selectedDate = new PropertyFilterDateRange(selectedDateAttrs);
  props.filter.filterValue = selectedDate;

  /**
   * TODO: date filters can all be expressed with the following operators:
   *
   * ==, !=, >=, ∋, ∌, <=
   * = (in the last, on)
   * != (not in the last, not on)
   * > (in the next, since)
   * ∋ (between)
   * ∌ (not between)
   * < (before the last, ending)
   *
   * These require additional changes to the API:
   *  - [!= last x days] not in the last: translates to not between
   *  - [< date] before date: translates to "ending at date"
   */
  const handleDatePickerChange = (ev: CustomEvent) => {
    var _filterValue$window;
    const dateRange: DateRange = ev.detail.dateRange;
    props.setTimePickerEnabled(ev.detail.isTimePickerEnabled);
    const {
      filterOperator
    } = props.filter;
    if (filterOperator === DateRangeType.Before) {
      dateRange.from = null;
    }
    const filterValue: PropertyFilterDateRange = props.filter.createDateRange(_objectSpread(_objectSpread({}, dateRange.getAttributes()), {}, {
      type: props.filter.filterOperator as DateRangeType
    }));
    const updatedFilter = props.filter.clonePropertyFilter({
      filterDateUnit: (_filterValue$window = filterValue.window) === null || _filterValue$window === void 0 ? void 0 : _filterValue$window.unit,
      filterValue
    });
    props.updateEntry(updatedFilter);
  };
  return <div className='filter-value-select-wrapper' data-sentry-component="FilterDateValue" data-sentry-source-file="date.tsx">
      <SelectReact className={clsx("filter-value-select", {
      'is-placeholder': !props.filter.isValid()
    })} elref='filterValueSelect' open={props.isActive} theme={props.hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select} neverShowCaret readonly={props.readonly} selectedOption={props.filter.isValid() ? print(getDateRangeLabel({
      dateRange: selectedDate,
      matchingPreset: null,
      projectAsOfDate: null /* project as of date; we set this to "null" because we're trialing a change where we show the project-as-of-date in the tooltip, not the label */,
      now: getCurrentMoment({
        localized: true
      }),
      formatMode: "none"
    })) : t("query-builder.value-placeholder")} onMenuChange={ev => props.onSelectMenuChange(ev)} data-sentry-element="SelectReact" data-sentry-source-file="date.tsx">
        {props.isActive && <DateRangePickerReact elref='filterDateRangePicker' singlePickerMode isTimePickerEnabled={props.isTimePickerEnabled} displayContext={DisplayContext.PropertyFilter} slot='body' dateRange={props.filter.isValid() && selectedDate.getAttributes()} trackedUseCase={TrackingUsageContext.QueryBuilder} onApply={ev => handleDatePickerChange(ev)} onCancel={() => props.setActiveEntry("close")} />}
      </SelectReact>
      {!props.isActive && props.filter.isValid() && <TooltipReact elref='dateRangeTooltip' placement={Placement.Bottom} lazyContentRenderer={getDateRangeTooltipRenderer(props.filter.filterValue as DateRange, currentProject)} />}
    </div>;
}